import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { 
  Heart, Moon, DollarSign, Trophy, HeartHandshake, 
  Sparkles, Shield, Users, Target, Infinity, Loader2 
} from "lucide-react";
import GlassCard from "@/components/GlassCard";
import NeonButton from "@/components/NeonButton";
import AudioPlayer from "@/components/AudioPlayer";
import FrequencySelector from "@/components/FrequencySelector";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Heart, Moon, DollarSign, Trophy, HeartHandshake, 
  Sparkles, Shield, Users, Target, Infinity
};

const voices = [
  { value: "shimmer", label: "Shimmer" },
  { value: "nova", label: "Nova" },
  { value: "echo", label: "Echo" },
  { value: "onyx", label: "Onyx" },
  { value: "alloy", label: "Alloy" },
  { value: "fable", label: "Fable" },
];

const categories = [
  "All",
  "Healing & Health",
  "Wealth & Abundance", 
  "Love & Relationships",
  "Confidence & Self-esteem",
  "Focus & Productivity",
  "Spiritual Growth"
];

const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedAudio, setGeneratedAudio] = useState(null);
  
  // Generation settings
  const [frequency, setFrequency] = useState(432);
  const [voice, setVoice] = useState("shimmer");
  const [speed, setSpeed] = useState(0.5);
  const [loopDuration, setLoopDuration] = useState(30);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load templates");
    }
  };

  const filteredTemplates = selectedCategory === "All" 
    ? templates 
    : templates.filter(t => t.category === selectedCategory);

  const openTemplateDialog = (template) => {
    setSelectedTemplate(template);
    setGeneratedAudio(null);
    setIsDialogOpen(true);
  };

  const handleGenerate = async () => {
    if (!selectedTemplate) return;
    
    setIsGenerating(true);
    try {
      const formData = new FormData();
      formData.append('template_id', selectedTemplate.id);
      formData.append('frequency', frequency);
      formData.append('loop_duration', loopDuration);
      formData.append('voice', voice);
      formData.append('speed', speed);

      const response = await axios.post(`${API}/generate-from-template`, formData);
      setGeneratedAudio(response.data);
      toast.success("Subliminal generated successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to generate subliminal");
    } finally {
      setIsGenerating(false);
    }
  };

  const getIcon = (iconName) => {
    const Icon = iconMap[iconName] || Sparkles;
    return Icon;
  };

  return (
    <div className="min-h-screen pt-20 md:pt-8 pb-32 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <p className="tagline-shimmer text-xs font-semibold uppercase mb-3">
            Expand Your Mind.
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            Template <span className="text-neon">Library</span>
          </h1>
          <p className="text-gray-400">
            Curated affirmations for every area of your life
          </p>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap justify-center gap-2 mb-10"
        >
          {categories.map((cat) => (
            <motion.button
              key={cat}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedCategory(cat)}
              data-testid={`category-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              className={`
                px-4 py-2 rounded-full text-sm transition-all duration-200
                ${selectedCategory === cat 
                  ? 'bg-violet-600 text-white' 
                  : 'glass text-gray-400 hover:text-white hover:bg-white/10'
                }
              `}
            >
              {cat}
            </motion.button>
          ))}
        </motion.div>

        {/* Templates Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template, index) => {
            const Icon = getIcon(template.icon);
            
            return (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <GlassCard 
                  className="h-full cursor-pointer group"
                  onClick={() => openTemplateDialog(template)}
                  data-testid={`template-${template.id}`}
                >
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${template.color}20` }}
                  >
                    <Icon size={24} style={{ color: template.color }} />
                  </div>
                  
                  <div className="mb-2">
                    <span className="text-xs text-gray-500">{template.category}</span>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-violet-400 transition-colors">
                    {template.title}
                  </h3>
                  
                  <p className="text-sm text-gray-400 mb-4">
                    {template.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {template.affirmations.length} affirmations
                    </span>
                    <span className="text-xs text-violet-400 opacity-0 group-hover:opacity-100 transition-opacity">
                      Click to preview →
                    </span>
                  </div>
                </GlassCard>
              </motion.div>
            );
          })}
        </div>

        {/* Template Detail Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="bg-[#0a0a0f] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedTemplate && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-white flex items-center gap-3">
                    {(() => {
                      const Icon = getIcon(selectedTemplate.icon);
                      return (
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${selectedTemplate.color}20` }}
                        >
                          <Icon size={20} style={{ color: selectedTemplate.color }} />
                        </div>
                      );
                    })()}
                    {selectedTemplate.title}
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-6 mt-4">
                  {/* Affirmations List */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-3">Affirmations</h4>
                    <div className="space-y-2">
                      {selectedTemplate.affirmations.map((aff, i) => (
                        <div 
                          key={i}
                          className="flex items-start gap-2 text-sm text-gray-400"
                        >
                          <span className="text-violet-500 mt-0.5">•</span>
                          <span>{aff}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Settings */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-2">Voice</label>
                      <Select value={voice} onValueChange={setVoice}>
                        <SelectTrigger data-testid="dialog-voice-select" className="bg-black/20 border-white/10 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a0a0f] border-white/10">
                          {voices.map((v) => (
                            <SelectItem key={v.value} value={v.value} className="text-white hover:bg-white/5">
                              {v.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs text-gray-500">Speed</label>
                        <span className="text-xs text-violet-400 font-mono">{speed.toFixed(2)}x</span>
                      </div>
                      <Slider
                        value={[speed]}
                        onValueChange={([v]) => setSpeed(v)}
                        min={0.25}
                        max={1}
                        step={0.05}
                        data-testid="dialog-speed-slider"
                      />
                    </div>
                  </div>

                  {/* Frequency */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Frequency</label>
                    <FrequencySelector value={frequency} onChange={setFrequency} showCustom={false} />
                  </div>

                  {/* Generate Button */}
                  <NeonButton
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    data-testid="dialog-generate-btn"
                    className="w-full flex items-center justify-center gap-2"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles size={18} />
                        Generate Subliminal
                      </>
                    )}
                  </NeonButton>

                  {/* Generated Audio */}
                  {generatedAudio && (
                    <AudioPlayer
                      audioBase64={generatedAudio.audio_base64}
                      title={generatedAudio.title}
                      frequency={generatedAudio.frequency}
                      loopEnabled={true}
                    />
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Templates;
